import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Header.css';

type Page = 'home' | 'archive' | 'new-arrivals' | 'band-tees' | 'about' | 'signup' | 'signin' | 'account' | 'admin' | 'checkout';

interface HeaderProps {
  onNavigate: (page: Page) => void;
  currentPage: Page;
  onSearch: (term: string) => void;
  searchTerm: string;
  onToggleCart: () => void;
  cartItemCount: number;
}

const Header: React.FC<HeaderProps> = ({ onNavigate, currentPage, onSearch, searchTerm, onToggleCart, cartItemCount }) => {
  const { isAuthenticated, signOut, user, isAdmin } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextSearchTerm = e.target.value;

    onSearch(nextSearchTerm);

    if (nextSearchTerm.trim() && currentPage !== 'archive') {
      onNavigate('archive');
      setIsMenuOpen(false);
    }
  };

  const handleSearchFocus = () => {
    if (currentPage !== 'archive') {
      onNavigate('archive');
    }
  };

  const handleNavigate = (page: Page) => {
    onNavigate(page);
    setIsMenuOpen(false);
  };

  const handleAccountClick = () => {
    if (isAuthenticated) {
      onNavigate('account');
    } else {
      onNavigate('signin');
    }
  };

  const primaryLinks: Array<{ label: string; page: Page; icon: string }> = [
    { label: 'HOME', page: 'home', icon: 'bi-house-door' },
    { label: 'ARCHIVE', page: 'archive', icon: 'bi-grid-3x3-gap' },
    { label: 'NEW ARRIVALS', page: 'new-arrivals', icon: 'bi-stars' },
    { label: 'BAND-TEES', page: 'band-tees', icon: 'bi-music-note-beamed' }
  ];

  return (
    <header className="header">
      <div className="header-topbar">
        <button
          className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={isMenuOpen}
        >
          <i className="bi bi-list menu-toggle-icon" aria-hidden="true"></i>
          <span className="menu-toggle-label">MENU</span>
        </button>

        <div className="header-brand">
          <button
            className={`logo-link ${currentPage === 'home' ? 'active' : ''}`}
            onClick={() => handleNavigate('home')}
          >
            <img 
              src="/logo.png" 
              alt="ANTI-STYLE HYPOTHESIS" 
              className="logo-image"
            />
          </button>
        </div>

        <div className="header-tools">
          <div className="cart-icon-container" onClick={onToggleCart}>
            <i className="bi bi-bag cart-icon icon" aria-hidden="true"></i>
            {cartItemCount > 0 && (
              <span className="cart-count">{cartItemCount}</span>
            )}
          </div>
          <button className="account-icon" onClick={handleAccountClick} aria-label="Open account">
            <i className="bi bi-person icon" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div className={`header-drawer-backdrop ${isMenuOpen ? 'open' : ''}`} onClick={() => setIsMenuOpen(false)}>
        <aside className={`header-drawer ${isMenuOpen ? 'open' : ''}`} onClick={(event) => event.stopPropagation()}>
          <div className="drawer-header">
            <button className="drawer-close" onClick={() => setIsMenuOpen(false)} aria-label="Close navigation menu">
              <i className="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>

          <div className="drawer-search">
            <div className="search-input-shell">
              <i className="bi bi-search search-input-icon" aria-hidden="true"></i>
              <input
                type="text"
                placeholder="SEARCH"
                className="search-input"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
              />
            </div>
          </div>

          <nav className="drawer-nav" aria-label="Main navigation">
            {primaryLinks.map((link) => (
              <button
                key={link.page}
                className={`drawer-link ${currentPage === link.page ? 'active' : ''}`}
                onClick={() => handleNavigate(link.page)}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="drawer-divider"></div>

          <div className="drawer-account-block">
            {isAuthenticated ? (
              <>
                <p className="drawer-user-label">
                  Signed in as {(user as any)?.user_metadata?.firstName || 'User'}
                </p>
                <button className={`drawer-link ${currentPage === 'account' ? 'active' : ''}`} onClick={() => handleNavigate('account')}>
                  ACCOUNT
                </button>
                {isAdmin && (
                  <button className={`drawer-link ${currentPage === 'admin' ? 'active' : ''}`} onClick={() => handleNavigate('admin')}>
                    ADMIN
                  </button>
                )}
                <button
                  className="drawer-action secondary"
                  onClick={async () => {
                    await signOut();
                    handleNavigate('home');
                  }}
                >
                  SIGN OUT
                </button>
              </>
            ) : (
              <>
                <button className={`drawer-link ${currentPage === 'signin' ? 'active' : ''}`} onClick={() => handleNavigate('signin')}>
                  SIGN IN
                </button>
                <button className={`drawer-link ${currentPage === 'signup' ? 'active' : ''}`} onClick={() => handleNavigate('signup')}>
                  CREATE ACCOUNT
                </button>
              </>
            )}
          </div>
        </aside>
      </div>

      
    </header>
  );
};

export default Header;
